import React from 'react';
import '../styles/SimilarityBadge.css';

// Similarity threshold constants (aligned with backend LegoTools.java)
// These thresholds must match the backend to ensure consistent color mapping
const THRESHOLD_NEARLY_IDENTICAL = 95;
const THRESHOLD_VERY_SIMILAR = 85;
const THRESHOLD_QUITE_SIMILAR = 75;
const THRESHOLD_SOMEWHAT_SIMILAR = 65;
const THRESHOLD_LOOSELY_SIMILAR = 55;

interface SimilarityBadgeProps {
  /**
   * Percentage value (0-100)
   */
  percentage: number;

  /**
   * Description category (e.g., "very-similar", "quite-similar")
   */
  description?: string;
}

/**
 * Get the color for a given percentage value.
 * Maps percentage to color according to similarity thresholds.
 *
 * Color mapping:
 * - 0-54%: Red (slightly similar)
 * - 55-64%: Orange (loosely similar)
 * - 65-74%: Yellow (somewhat similar)
 * - 75-84%: Light green (quite similar)
 * - 85-94%: Green (very similar)
 * - 95-100%: Dark green (nearly identical)
 */
const getColorForPercentage = (percentage: number): string => {
  if (percentage >= THRESHOLD_NEARLY_IDENTICAL) return '#16a34a'; // Dark green - nearly identical
  if (percentage >= THRESHOLD_VERY_SIMILAR) return '#22c55e'; // Green - very similar
  if (percentage >= THRESHOLD_QUITE_SIMILAR) return '#4ade80'; // Light green - quite similar
  if (percentage >= THRESHOLD_SOMEWHAT_SIMILAR) return '#eab308'; // Yellow - somewhat similar
  if (percentage >= THRESHOLD_LOOSELY_SIMILAR) return '#f97316'; // Orange - loosely similar
  return '#ef4444'; // Red - slightly similar
};

/**
 * Progress bar component for displaying similarity scores.
 * Renders a horizontal bar with solid color fill based on percentage.
 *
 * Color mapping:
 * - 0-54%: Red (slightly similar)
 * - 55-64%: Orange (loosely similar)
 * - 65-74%: Yellow (somewhat similar)
 * - 75-84%: Light green (quite similar)
 * - 85-94%: Green (very similar)
 * - 95-100%: Dark green (nearly identical)
 */
export const SimilarityBadge: React.FC<SimilarityBadgeProps> = ({
  percentage,
  description,
}) => {
  // Clamp percentage to 0-100 range
  const clampedPercentage = Math.max(0, Math.min(100, percentage));

  // Get color based on percentage
  const fillColor = getColorForPercentage(clampedPercentage);

  // Format description for tooltip (convert "very-similar" to "Very Similar")
  const tooltipText = description
    ? description.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    : `${clampedPercentage.toFixed(1)}% similarity`;

  return (
    <span className="similarity-badge" role="img" aria-label={`${clampedPercentage}% similarity`} title={tooltipText}>
      <div className="similarity-badge__bar">
        <div
          className="similarity-badge__fill"
          style={{
            width: `${clampedPercentage}%`,
            backgroundColor: fillColor
          }}
          aria-hidden="true"
        />
      </div>
      <span className="similarity-badge__text">{clampedPercentage.toFixed(1)}%</span>
    </span>
  );
};

export default SimilarityBadge;
